import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  MessageSquare,
  DollarSign,
  Users,
  Settings,
  ChevronLeft,
  FileText,
  TrendingUp,
  Sparkles,
  Plus,
  Search,
  User,
  LogOut,
  Bell,
  HelpCircle,
  BarChart3,
  Moon,
  Sun,
} from 'lucide-react';
import { getThemeColor, getRadius, getTransition, getFontSize, getFontWeight } from '../lib/theme';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { useAuth } from '../contexts/AuthContext';
import SimpleSettingsModal from './settings/SimpleSettingsModal';
import logo from '../assets/logo.png';

const MobileDock = ({ navigation, location, isDark, handleItemClick }) => {
  return (
    <motion.div
      className="fixed bottom-0 left-0 right-0 z-50 w-screen"
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
    >
      <div
        className="flex items-center justify-around w-screen px-0 py-2 backdrop-blur-xl border-t"
        style={{
          backgroundColor: isDark ? 'rgba(26, 26, 26, 0.95)' : 'rgba(245, 245, 245, 0.95)',
          borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          margin: 0,
          paddingLeft: 0,
          paddingRight: 0
        }}
      >
        {navigation.map((item) => {
          const active = location.pathname === item.path;
          const Icon = item.icon;

          return (
            <motion.button
              key={item.id}
              onClick={() => handleItemClick(item.path)}
              className="relative flex flex-col items-center gap-1 w-full max-w-[20%] px-0"
              whileTap={{ scale: 0.95 }}
              style={{ margin: 0 }}
            >
              {active && (
                <motion.div
                  className="absolute -top-2 left-1/2 w-1 h-1 rounded-full"
                  style={{
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                    transform: 'translateX(-50%)'
                  }}
                  layoutId="mobileIndicator"
                  transition={{
                    type: "spring",
                    stiffness: 350,
                    damping: 30,
                    mass: 0.8
                  }}
                />
              )}
              <div
                className={`w-9 h-9 rounded-xl flex items-center justify-center shadow-sm ${getTransition('default')}`}
                style={{
                  backgroundColor: active
                    ? (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)')
                    : (isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'),
                  boxShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.05)'
                }}
              >
                <Icon
                  className={`w-5 h-5 ${getTransition('default')}`}
                  style={{
                    color: active
                      ? (isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)')
                      : (isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)')
                  }}
                />
              </div>
              <span
                className="text-[10px] font-medium truncate w-full text-center"
                style={{
                  color: active
                    ? (isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)')
                    : (isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)')
                }}
              >
                {item.name}
              </span>
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
};

const Sidebar = ({ onConfigClick, children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const isMobile = useMediaQuery('(max-width: 640px)');
  const isTablet = useMediaQuery('(min-width: 641px) and (max-width: 1024px)');
  const [isDark, setIsDark] = useState(() => {
    // Check if we're in the browser
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark');
    }
    // Default to system preference if available
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };

    // Initial check
    checkDarkMode();

    // Create observer for dark mode changes
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      if (!localStorage.getItem('theme')) {
        setIsDark(e.matches);
      }
    };
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      observer.disconnect();
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, []);

  const navigation = [
    {
      id: 'overview',
      name: 'Overview',
      icon: LayoutDashboard,
      path: '/',
    },
    {
      id: 'revenue',
      name: 'Revenue',
      icon: DollarSign,
      path: '/revenue',
    },
    {
      id: 'feedback',
      name: 'Feedback',
      icon: MessageSquare,
      path: '/feedback',
    },
    {
      id: 'users',
      name: 'Users',
      icon: Users,
      path: '/users',
    },
  ];

  const handleItemClick = (path) => {
    navigate(path);
    if (isMobile || isTablet) {
      setIsCollapsed(true);
    }
  };

  const handleLogout = () => {
    logout();
  };

  if (isMobile) {
    return (
      <div className="flex flex-col min-h-screen">
        <div className="flex-1 pb-[120px]">
          {children}
        </div>
        <MobileDock
          navigation={navigation}
          location={location}
          isDark={isDark}
          handleItemClick={handleItemClick}
        />
      </div>
    );
  }

  return (
    <motion.div
      className={`relative flex flex-col h-full backdrop-blur-xl border-r overflow-hidden ${getTransition('default')}`}
      style={{
        backgroundColor: isDark ? 'rgba(26, 26, 26, 0.8)' : 'rgba(245, 245, 245, 0.8)',
        borderColor: getThemeColor('border', isDark),
        width: isCollapsed ? (isTablet ? 80 : 68) : (isTablet ? 240 : 280)
      }}
      animate={{
        width: isCollapsed ? (isTablet ? 80 : 68) : (isTablet ? 240 : 280),
        transition: {
          type: "spring",
          stiffness: 400,
          damping: 40
        }
      }}
    >
      {/* Header */}
      <motion.div
        className="relative flex items-center justify-between px-4 py-4 border-b"
        style={{
          borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          backgroundColor: isDark ? 'rgba(26, 26, 26, 0.5)' : 'rgba(245, 245, 245, 0.5)'
        }}
        layout
      >
        <AnimatePresence mode="wait">
          {!isCollapsed && (
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{ duration: 0.15 }}
              className="flex items-center gap-3"
            >
              <div
                className="w-8 h-8 rounded-xl flex items-center justify-center shadow-sm"
                style={{
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                  boxShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.05)'
                }}
              >
                <img
                  src={logo}
                  alt="Logo"
                  className="w-6 h-6 object-contain"
                  style={{ filter: isDark ? 'brightness(1.2)' : 'none' }}
                />
              </div>
              {!isTablet && (
                <div>
                  <h1
                    className="text-sm tracking-tight"
                    style={{
                      color: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                      fontWeight: getFontWeight('bold')
                    }}
                  >
                    🦁 LionThrone
                  </h1>
                  <p
                    className="text-xs"
                    style={{
                      color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)',
                      fontWeight: getFontWeight('medium')
                    }}
                  >
                    ResumeNL Throne
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        <motion.button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className={`w-8 h-8 rounded-xl flex items-center justify-center border ${getTransition('default')} hover:bg-opacity-80 shadow-sm`}
          style={{
            backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
            borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
            color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)',
            boxShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.05)'
          }}
          whileTap={{ scale: 0.95 }}
        >
          <motion.div
            animate={{ rotate: isCollapsed ? 180 : 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 40 }}
          >
            <ChevronLeft className="w-4 h-4" />
          </motion.div>
        </motion.button>
      </motion.div>

      {/* Quick Actions */}
      <AnimatePresence>
        {!isCollapsed && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ delay: 0.05, duration: 0.15 }}
            className="relative px-4 py-4"
          >
            <div className="space-y-2.5">
              <button
                className={`w-full flex items-center gap-3 h-9 px-3.5 border rounded-xl text-sm ${getTransition('default')} group hover:bg-opacity-80 shadow-sm`}
                style={{
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                  borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                  color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)',
                  boxShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.05)'
                }}
              >
                <Search
                  className="w-4 h-4"
                  style={{ color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)' }}
                />
                <span className="flex-1 text-left" style={{ fontWeight: getFontWeight('medium') }}>
                  Quick search...
                </span>
                <kbd
                  className="px-2 py-1 text-xs border rounded-lg font-semibold"
                  style={{
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
                    borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
                    color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)'
                  }}
                >
                  ⌘K
                </kbd>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Navigation */}
      <div className="flex-1 px-3 py-2 overflow-y-auto">
        <nav className="space-y-1">
          {navigation.map((item) => {
            const active = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => handleItemClick(item.path)}
                className={`group relative flex items-center gap-3.5 w-full px-3.5 py-3 rounded-xl text-sm ${getTransition('default')} ${isCollapsed ? "justify-center px-2" : ""}
                  hover:bg-opacity-80 shadow-sm`}
                style={{
                  backgroundColor: active
                    ? (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)')
                    : 'transparent',
                  color: active
                    ? (isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)')
                    : (isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)'),
                  borderColor: active
                    ? (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)')
                    : 'transparent',
                  borderWidth: active ? '1px' : '0',
                  fontWeight: getFontWeight('medium'),
                  boxShadow: active
                    ? (isDark ? '0 2px 4px rgba(0, 0, 0, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.05)')
                    : 'none'
                }}
              >
                {/* Active indicator */}
                {active && (
                  <motion.div
                    className="absolute left-0 w-1 h-8 rounded-full"
                    style={{
                      backgroundColor: isDark ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.8)',
                      top: '50%',
                      transform: 'translateY(-50%)'
                    }}
                    initial={{ opacity: 0, scaleY: 0 }}
                    animate={{
                      opacity: 1,
                      scaleY: 1,
                      transition: { type: "spring", stiffness: 400, damping: 40 }
                    }}
                    exit={{ opacity: 0, scaleY: 0 }}
                  />
                )}
                <Icon className="w-5 h-5" />
                {!isCollapsed && <span>{item.name}</span>}
              </button>
            );
          })}
          {/* Settings button at the bottom */}
          <button
            onClick={() => setSettingsOpen(true)}
            className={`group relative flex items-center gap-3.5 w-full px-3.5 py-3 rounded-xl text-sm mt-8 ${getTransition('default')} ${isCollapsed ? "justify-center px-2" : ""}
              hover:bg-opacity-80 shadow-sm`}
            style={{
              backgroundColor: 'transparent',
              color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)',
              fontWeight: getFontWeight('medium'),
            }}
          >
            <Settings className="w-5 h-5" />
            {!isCollapsed && <span>Settings</span>}
          </button>
        </nav>
      </div>

      {/* Footer */}
      <div
        className="px-3 py-4 border-t"
        style={{
          borderColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
          backgroundColor: isDark ? 'rgba(26, 26, 26, 0.5)' : 'rgba(245, 245, 245, 0.5)'
        }}
      >
        <div className="space-y-1">
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-sm ${getTransition('default')} hover:bg-opacity-80 shadow-sm`}
            style={{
              color: isDark ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.5)',
              backgroundColor: 'transparent',
              boxShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.2)' : '0 2px 4px rgba(0, 0, 0, 0.05)'
            }}
          >
            <LogOut className="w-4 h-4" />
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      </div>
      {/* Render the modal at the bottom of Sidebar */}
      <SimpleSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
    </motion.div>
  );
};

export default Sidebar;